import { HttpClient } from '@angular/common/http';
import { inject, Injectable, Signal, signal } from '@angular/core';
import { Invoice, InvoiceDetails, Obligation } from '@core/models';
import { environment } from '@env/environment';
import { UserStatus } from '@shared/enums';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { PaginationDataService } from './pagination-data.service';
@Injectable({
  providedIn: 'root',
})
export class ObligationApiService {
  private _apiUrl = `${environment.baseUrl}/invoices`;

  // Obligaciones con paginación
  private _obligationData = inject(PaginationDataService<Obligation>);

  // Obligaciones sin paginación
  private _obligationDataRaw = signal<Obligation[]>([]);

  private _statusFilter = signal<number[]>([UserStatus.ACTIVE]);
  private _invoice = signal<Invoice | null>(null);

  constructor(private http: HttpClient) {
    this._obligationData.setFilterOptions({
      filterFunctionSearch: (data, query) => this.filterFunctionSearch(data, query),
      applyAdditionalFilters: (items) => this.applyAdditionalFilters(items),
    });
  }

  /** Operaciones */
  fetchInvoiceDetails(
  entityId: number, 
  year: number, 
  month: number,
  percentageId?: number 
): Observable<InvoiceDetails> {
  const percentage = percentageId || 0; // Si no hay porcentaje, usar 0
  
  return this.http
    .get<InvoiceDetails>(`${this._apiUrl}/${entityId}/${year}/${month}/${percentage}`)
    .pipe(
      tap((data) => this.setData(data)),
      catchError((err) => throwError(() => err.error?.message)),
    );
}

  loadInvoice(form: FormData): Observable<InvoiceDetails> {
    return this.http.post<InvoiceDetails>(`${this._apiUrl}/load`, form).pipe(
      tap((data) => this.setData(data)),
      catchError((err) => throwError(() => err.error.message)),
    );
  }

  approveInvoice(invoiceId: number): Observable<Invoice> {
    const form = new FormData();
    form.append('invoiceId', invoiceId.toString());

    return this.http.post<Invoice>(`${this._apiUrl}/approve`, form).pipe(
      tap((data) => this._invoice.set(data)),
      catchError((err) => throwError(() => err.error.message)),
    );
  }

  setData(data: InvoiceDetails) {
    if (data && data.invoice) {
      // con paginación
      this._obligationData.setItems(data.obligation);

      // sin paginación
      this._obligationDataRaw.set(data.obligation);

      this._invoice.set(data.invoice);
    }
  }

  clearData() {
    this._obligationData.setItems([]);
    this._obligationDataRaw.set([]);
    this._invoice.set(null);
  }

  clearInvoice(invoiceId: number) {
    return this.http.delete(`${this._apiUrl}/discard/${invoiceId}`);
  }

  // =====================
  // GETTERS
  // =====================

  get invoice(): Signal<Invoice | null> {
    return this._invoice;
  }

  // Obligaciones paginadas
  get obligations(): Signal<Obligation[]> {
    return this._obligationData.getItemsWithFilter();
  }

  // Obligaciones completas sin paginar
  get obligationsRaw(): Signal<Obligation[]> {
    return this._obligationDataRaw;
  }

  // Paginación
  get totalRecords(): Signal<number> {
    return this._obligationData.getTotalRecords();
  }

  get recordsPerPage(): Signal<number> {
    return this._obligationData.getRecordsPerPage();
  }

  get sortField(): Signal<keyof Obligation | null> {
    return this._obligationData.getSortField();
  }

  get sortDirection(): Signal<'asc' | 'desc'> {
    return this._obligationData.getSortDirection();
  }

  toggleSort(field: keyof Obligation) {
    this._obligationData.toggleSort(field);
  }

  setCurrentPage(page: number): void {
    this._obligationData.setCurrentPage(page);
  }

  setRecordsPerPage(records: number): void {
    this._obligationData.setRecordsPerPage(records);
  }

  // Filtros
  get statusFilter(): Signal<number[] | null> {
    return this._statusFilter;
  }

  setStatusFilter(statusId: number[]): void {
    this._statusFilter.set(statusId);
  }

  private filterFunctionSearch(data: Obligation, query: string): boolean {
    return (
      data.obligationNumber.toString().includes(query) ||
      data.associateId.toLowerCase().includes(query)
    );
  }

  private applyAdditionalFilters(items: Obligation[]): Obligation[] {
    let filtered = [...items];
    // podrías aplicar filtros acá si quieres
    return filtered;
  }
}
